import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../../../components/ui/button';
import { MAX_CELL_ROWS } from '../../../../lib/publishing/calendarBuckets';
import { externalCacheNote, externalStaleMs } from '../../../../lib/publishing/externalFreshness';
import { monthGridRange } from '../../../../lib/publishing/externalPosts';
import { buildClubMonth, monthLabel, postsOnDay, shiftMonth, toExternalCalendarPost, type ClubCalendarCell } from '../../../../lib/socialCalendar';
import { EXTERNAL_LIMITS_NOTE, EXTERNAL_READ_ONLY_NOTE, externalCappedSentence, externalFailureSentence } from '../../../../lib/socialExternalNotice';
import { PLATFORM_LABELS } from '../../../../lib/socialPostView';
import type { CalendarPost } from '../../../../types/publishing';
import { ClubCalendarChip, ClubChipHoverCard, type ChipHover } from './ClubCalendarChip';
import { canShowStats, ClubPostStatsDialog } from './ClubPostStatsDialog';
import { PostRow } from './PostRow';
import { socialApi, type ClientPost } from './socialApi';

// The club's content calendar: posts made THROUGH Kyck, plus posts published directly
// on the platforms (read-only, shown apart in purple, permalink out and nothing else).
// Click a day to see its posts. NO drag: moving a scheduled post to the wrong day would
// publish it at the wrong time, so the date changes in the composer, where the confirm
// step is. The native-posts query is keyed by ACCOUNT ONLY: the vendor call returns the
// whole retained history whatever month is showing, so a month flip costs nothing, and
// the lifetime (externalStaleMs) is the shared one.

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayLabel(date: Date): string {
  return date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}

function cellLabel(cell: ClubCalendarCell): string {
  const n = cell.postCount;
  return `${dayLabel(cell.date)}, ${n === 0 ? 'no posts' : n === 1 ? '1 post' : `${n} posts`}`;
}

export function ClubCalendar({ posts, accountId }: { posts: ClientPost[]; accountId: string }) {
  const [today] = useState(() => new Date());
  const [cursor, setCursor] = useState(() => ({ year: today.getFullYear(), month0: today.getMonth() }));
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [hover, setHover] = useState<ChipHover | null>(null);
  const [statsPostId, setStatsPostId] = useState<string | null>(null);

  const external = useQuery({
    queryKey: ['social-external-posts', accountId],
    queryFn: () => socialApi.externalPosts(accountId),
    staleTime: externalStaleMs(monthGridRange(cursor.year, cursor.month0), Date.now()),
    retry: false,
  });
  const externalPosts = useMemo(
    () => (external.data?.posts ?? []).map((p) => toExternalCalendarPost(p, accountId)),
    [external.data, accountId],
  );
  const permalinks = new Map((external.data?.posts ?? []).map((p) => [p.id, p.permalink]));
  const failureNote = externalFailureSentence(external.data?.failures ?? []);
  const cappedNote = externalCappedSentence(external.data?.capped ?? false);
  const month = buildClubMonth(posts, cursor.year, cursor.month0, today, externalPosts);
  const byId = new Map(posts.map((p) => [p.id, p]));
  const cells = month.weeks.flat();
  const selected = cells.find((c) => c.key === selectedKey) ?? null;
  const go = (delta: number) => {
    setCursor((c) => shiftMonth(c.year, c.month0, delta));
    setSelectedKey(null);
  };

  return (
    <div className="space-y-4" data-testid="club-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => go(-1)} aria-label="Previous month">
          Previous
        </Button>
        <h3 className="min-w-[10rem] text-center text-base font-semibold text-foreground" aria-live="polite">
          {monthLabel(cursor.year, cursor.month0)}
        </h3>
        <Button type="button" variant="outline" size="sm" onClick={() => go(1)} aria-label="Next month">
          Next
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setCursor({ year: today.getFullYear(), month0: today.getMonth() });
            setSelectedKey(null);
          }}
        >
          This month
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[40rem] table-fixed border-collapse" aria-label={`Posts in ${monthLabel(cursor.year, cursor.month0)}`}>
          <thead>
            <tr>
              {WEEKDAYS.map((d) => (
                <th key={d} scope="col" className="border border-border bg-muted px-1 py-1 text-xs font-semibold text-muted-foreground">
                  {d}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {month.weeks.map((week) => (
              <tr key={week[0]?.key}>
                {week.map((cell) => {
                  const isSelected = cell.key === selectedKey;
                  const shown = cell.rows.slice(0, MAX_CELL_ROWS);
                  const more = cell.rows.length - shown.length;
                  return (
                    <td key={cell.key} className={`border border-border p-0 align-top ${cell.inMonth ? '' : 'bg-muted/40'}`}>
                      {/* The cell is not one big button: its chips are buttons of their own
                          (hover shows the post, click opens performance), and a button cannot
                          hold a button. The day number is the keyboard route to the day. */}
                      <div
                        className={`h-full min-h-28 p-1 text-xs ${isSelected ? 'bg-accent' : 'hover:bg-accent/50'}`}
                        onClick={() => setSelectedKey(isSelected ? null : cell.key)}
                      >
                        <button
                          type="button"
                          aria-label={cellLabel(cell)}
                          aria-pressed={isSelected}
                          data-testid={`day-${cell.key}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedKey(isSelected ? null : cell.key);
                          }}
                          className={`inline-block rounded px-1 font-semibold ${cell.isToday ? 'bg-primary text-primary-foreground' : cell.inMonth ? 'text-foreground' : 'text-muted-foreground'}`}
                        >
                          {cell.date.getDate()}
                        </button>
                        {shown.map((r) => (
                          <ClubCalendarChip
                            key={r.rowKey}
                            row={r}
                            onHover={setHover}
                            onOpen={() => {
                              setHover(null);
                              if (canShowStats(r.post.origin, r.post.status) && byId.has(r.post.id)) setStatsPostId(r.post.id);
                              else setSelectedKey(cell.key);
                            }}
                          />
                        ))}
                        {more > 0 && <span className="mt-0.5 block px-1 text-muted-foreground">+{more} more</span>}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-1 text-xs text-muted-foreground" data-testid="calendar-scope-note">
        <p>{EXTERNAL_READ_ONLY_NOTE} To change the date of a Kyck post, open it.</p>
        <p data-testid="external-limits">{EXTERNAL_LIMITS_NOTE}</p>
        <p data-testid="external-cache-note">
          {externalCacheNote(externalStaleMs(monthGridRange(cursor.year, cursor.month0), Date.now()))}{' '}
          <Button type="button" variant="outline" size="sm" disabled={external.isFetching} onClick={() => void external.refetch()}>
            {external.isFetching ? 'Checking...' : 'Refresh'}
          </Button>
        </p>
        {external.isError && (
          <p role="alert" data-testid="external-error" className="text-destructive">
            We could not load posts made directly on the platforms. Posts made with Kyck are shown below. Try Refresh.
          </p>
        )}
        {failureNote && (
          <p role="alert" data-testid="external-failures" className="rounded bg-tone-warning px-2 py-1 text-tone-warning-foreground">
            {failureNote}
          </p>
        )}
        {cappedNote && (
          <p role="status" data-testid="external-capped" className="rounded bg-tone-warning px-2 py-1 text-tone-warning-foreground">
            {cappedNote}
          </p>
        )}
      </div>

      {hover && <ClubChipHoverCard hover={hover} />}
      {statsPostId && byId.get(statsPostId) && <ClubPostStatsDialog post={byId.get(statsPostId)!} onClose={() => setStatsPostId(null)} />}

      {selected && (
        <section aria-label={`Posts on ${dayLabel(selected.date)}`} data-testid="day-panel" className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">{dayLabel(selected.date)}</h3>
          {selected.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing is posted or scheduled for this day.</p>
          ) : (
            <ul className="space-y-3">
              {postsOnDay(selected).map(({ post }) => {
                if (post.origin === 'external') {
                  return <ExternalPostRow key={post.id} post={post} permalink={permalinks.get(post.id) ?? null} />;
                }
                const full = byId.get(post.id);
                return full ? <PostRow key={post.id} post={full} accountParam={accountId} /> : null;
              })}
            </ul>
          )}
        </section>
      )}

      {month.undated.length > 0 && (
        <section aria-label="Posts with no date" data-testid="undated" className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground">No date yet</h3>
          <p className="text-xs text-muted-foreground">Drafts that are not scheduled for a day.</p>
          <ul className="space-y-3">
            {month.undated.map((p) => {
              const full = byId.get(p.id);
              return full ? <PostRow key={p.id} post={full} accountParam={accountId} /> : null;
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

/** A natively published post: read-only. The only action is the link out. */
function ExternalPostRow({ post, permalink }: { post: CalendarPost; permalink: string | null }) {
  const where = post.platforms.map((p) => PLATFORM_LABELS[p] ?? p).join(', ');
  return (
    <li className="rounded-md border border-dashed border-tone-external-border bg-tone-external/40 p-3" data-testid="external-post-row">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-tone-external px-2 py-0.5 text-xs font-semibold text-tone-external-foreground">Published on the platform</span>
        <span className="text-xs text-muted-foreground">{where}</span>
        <span className="text-xs text-muted-foreground">Not made with Kyck</span>
      </div>
      {post.media.length > 0 && (
        <div className="mt-2 flex gap-2">
          {post.media.slice(0, 4).map((m) => (
            <img key={m.url} src={m.thumbnailUrl ?? m.url} alt={m.altText ?? ''} loading="lazy" className="h-16 w-16 rounded object-cover" />
          ))}
        </div>
      )}
      <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-foreground">{post.content || '(no caption)'}</p>
      {permalink && (
        <a className="mt-2 inline-block text-sm underline" href={permalink} target="_blank" rel="noreferrer">
          See on {where || 'the platform'}
        </a>
      )}
    </li>
  );
}
